/* eslint-disable no-empty */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import style from './password.module.scss';
import { useAppSelector } from 'store/hooks';
import { selectIsAuth } from 'store/auth/authSlice';
import * as yup from 'yup';
import Header from 'components/header/header';
import Footer from 'components/footer/footer';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import FormTextField from 'controls/textField/formTextField';
import FormPassword from 'controls/password/formPassword';
import { getErrorsProps } from 'utils/utils';
import SubmitButton from 'controls/buttons/submit';
import { passwordAPI } from 'api/Api';
import CircularLoader from 'components/loader/circular';
import SuccessModal from 'components/modal/successModal';
import { useNavigate } from 'react-router-dom';
import ErrorModal from 'components/modal/errorModal';
import cx from 'classnames';

type Step = '0' | '1' | '2';

let interval: any = null;
let intervalDone = false;

const schemas: {
  recover: { [key in Step]?: any };
  change: { [key in Step]?: any };
} = {
  recover: {
    '0': {
      email: yup
        .string()
        .required('Поле не заполнено')
        .email('Неверный формат'),
    },
    '1': {
      code: yup.string().required('Поле не заполнено'),
    },
    '2': {
      password1: yup
        .string()
        .required('Поле не заполнено')
        .min(8, 'Пароль должен содержать минимум 8 символов'),
      password2: yup
        .string()
        .required('Поле не заполнено')
        .oneOf([yup.ref('password1'), null], 'Пароли не совпадают'),
    },
  },
  change: {
    '0': {
      old_password: yup
        .string()
        .required('Поле не заполнено')
        .test('validator', 'Старый пароль указан неверно', async (value) => {
          if (intervalDone) {
            intervalDone = false;

            try {
              const result = await passwordAPI.checkOld(value || '');

              return result;
            } catch (e) {
              return false;
            }
          }

          return true;
        }),
      password1: yup
        .string()
        .required('Поле не заполнено')
        .notOneOf(
          [yup.ref('old_password'), null],
          'Ваш новый пароль не должен совпадать со старым'
        )
        .min(8, 'Пароль должен содержать минимум 8 символов'),
      password2: yup
        .string()
        .required('Поле не заполнено')
        .oneOf([yup.ref('password1'), null], 'Пароли не совпадают'),
    },
  },
};

const buttonText: {
  recover: { [key in Step]?: any };
  change: { [key in Step]?: any };
} = {
  recover: {
    '0': 'Отправить',
    '1': 'Проверить',
    '2': 'Восстановить пароль',
  },
  change: {
    '0': 'Создать пароль',
  },
};

export default function Password() {
  const isAuth = useAppSelector(selectIsAuth);

  const [email, setEmail] = useState<string>('');
  const [step, setStep] = useState<Step>('0');
  const [type, setType] = useState<'change' | 'recover'>(
    isAuth ? 'change' : 'recover'
  );
  const [isPending, setIsPending] = useState(false);
  const [isLinkPending, setIsLinkPending] = useState(false);
  const [currentModal, setCurrentModal] = useState<null | React.ReactElement>(
    null
  );
  const navigate = useNavigate();

  useEffect(() => setType(isAuth ? 'change' : 'recover'), [isAuth]);

  const text = isAuth ? 'Создание нового пароля' : 'Восстановление пароля';

  const schema = yup.object({
    ...schemas[type][step],
  });

  const methods = useForm({
    mode: 'all',
    criteriaMode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      code: '',
      password1: '',
      password2: '',
      old_password: '',
    },
  });

  const onSubmit = async (data: any) => {
    switch (type) {
      case 'recover':
        switch (step) {
          case '0':
            setIsPending(true);
            try {
              await passwordAPI.recover({
                step: step,
                fields: {
                  email: data.email,
                },
              });
              setEmail(data.email);
              setStep('1');
            } catch (e: any) {
              let text = 'Упс, кажется что то пошло не так';
              switch (e?.response?.status) {
                case 404:
                  text = `Аккаунт с почтой ${email} не найден`;
                  break;
              }
              setCurrentModal(
                <ErrorModal
                  onClose={() => setCurrentModal(null)}
                  errorText={text}
                ></ErrorModal>
              );
            }

            setIsPending(false);
            break;
          case '1':
            setIsPending(true);
            try {
              const res = await passwordAPI.recover({
                step: step,
                fields: {
                  code: data.code,
                  email,
                },
              });

              if (res === 'success') {
                setStep('2');
              } else if (res === 'error_incorrect_code') {
                setCurrentModal(
                  <ErrorModal
                    onClose={() => setCurrentModal(null)}
                    errorText="Неверно указан код"
                  ></ErrorModal>
                );
              }
            } catch (e) {
              setCurrentModal(
                <ErrorModal
                  onClose={() => setCurrentModal(null)}
                  errorText="Упс, кажется что то пошло не так"
                ></ErrorModal>
              );
            }
            setIsPending(false);
            break;
          case '2':
            setIsPending(true);
            try {
              await passwordAPI.recover({
                step: step,
                fields: {
                  new_password: data.password1,
                  email,
                },
              });
              setCurrentModal(
                <SuccessModal
                  onClose={() => {
                    setCurrentModal(null);
                    navigate('/login', {
                      replace: false,
                    });
                  }}
                  successText="Пароль успешно изменен"
                ></SuccessModal>
              );
            } catch (e) {
              setCurrentModal(
                <ErrorModal
                  onClose={() => setCurrentModal(null)}
                  errorText="Упс, кажется что то пошло не так"
                ></ErrorModal>
              );
            }

            setIsPending(false);
            break;
        }
        break;
      case 'change':
        setIsPending(true);
        await passwordAPI.change({
          old_password: data.old_password,
          new_password: data.password1,
        });
        setIsPending(false);
        setCurrentModal(
          <SuccessModal
            onClose={() => {
              setCurrentModal(null);
              navigate('/cabinet', {
                replace: false,
              });
            }}
            successText="Пароль успешно изменен"
          ></SuccessModal>
        );
        break;
    }
  };

  const adaptedErrors = getErrorsProps(methods.formState.errors);

  return (
    <div className={cx(style.container, 'adaptive-password__container')}>
      {currentModal !== null && currentModal}
      {isAuth && <Header></Header>}
      <div className={cx(style.padding, 'adaptive-password__padding')}>
        <FormProvider {...methods}>
          <form
            className={style.form}
            onSubmit={methods.handleSubmit(onSubmit)}
          >
            {/* Форма смены пароля для авторизованных пользователей */}
            {isAuth ? (
              <>
                <span className={cx(style.mainText, 'adaptive-f32')}>
                  {text}
                </span>
                <FormPassword
                  name="old_password"
                  rules={{ required: true }}
                  inner_props={{
                    className: cx(style.field, 'adaptive-password__field'),
                    input_label_props: {
                      children: 'Введите старый пароль',
                    },
                    input_props: {
                      ...adaptedErrors['old_password'],
                      onChange: () => {
                        clearInterval(interval);
                        intervalDone = false;

                        interval = setTimeout(() => {
                          intervalDone = true;
                          methods.trigger('old_password');
                        }, 500);
                        methods.trigger('password1');
                      },
                    },
                  }}
                ></FormPassword>

                <FormPassword
                  name="password1"
                  rules={{ required: true }}
                  inner_props={{
                    className: cx(style.field, 'adaptive-password__field'),
                    input_label_props: {
                      children: 'Введите новый пароль',
                    },
                    input_props: {
                      ...adaptedErrors['password1'],
                      onChange: () => {
                        methods.trigger('password2');
                      },
                    },
                  }}
                ></FormPassword>

                <FormPassword
                  name="password2"
                  rules={{ required: true }}
                  inner_props={{
                    className: cx(style.field, 'adaptive-password__field'),
                    input_label_props: {
                      children: 'Введите новый пароль повторно',
                    },
                    input_props: {
                      ...adaptedErrors['password2'],
                    },
                  }}
                ></FormPassword>
              </>
            ) : (
              // Форма восстановления пароля для не авторизованных пользователей
              <>
                <span className={style.mainText}>{text}</span>
                {step === '0' && (
                  <>
                    <span className={style.secondText}>
                      Введите адрес электронной почты, который вы указывали при
                      регистрации
                    </span>
                    <FormTextField
                      name="email"
                      rules={{ required: true }}
                      inner_props={{
                        className: cx(style.field, 'adaptive-password__field'),
                        InputProps: {
                          placeholder: 'example@transport.mos.ru',
                        },
                        ...adaptedErrors['email'],
                      }}
                    ></FormTextField>
                    <div className={style.help}>
                      <span>
                        Мы вышлем код подтверждения на вашу почту в течение 30
                        секунд
                      </span>
                    </div>
                  </>
                )}

                {step === '1' && (
                  <>
                    <span className={style.secondText}>
                      Мы выслали проверочный код на ваш адрес электронной почты
                    </span>
                    <FormTextField
                      name="code"
                      rules={{ required: true }}
                      inner_props={{
                        className: cx(style.field, 'adaptive-password__field'),
                        InputProps: {
                          placeholder: '******',
                        },
                        ...adaptedErrors['code'],
                      }}
                    ></FormTextField>
                    <div className={style.help}>
                      <span>Не получили код?</span>
                      {isLinkPending ? (
                        <CircularLoader className={style.loader} size={16} />
                      ) : (
                        <a
                          onClick={async () => {
                            setIsLinkPending(true);
                            await passwordAPI.recover({
                              step: '0',
                              fields: {
                                email: email,
                              },
                            });
                            setIsLinkPending(false);
                          }}
                        >
                          Отправить повторно
                        </a>
                      )}
                    </div>
                  </>
                )}

                {step === '2' && (
                  <>
                    <span className={style.secondText}>
                      Для надежности, используйте пароль, содержащий минимум 8
                      символов
                    </span>
                    <FormPassword
                      name="password1"
                      rules={{ required: true }}
                      inner_props={{
                        className: style.field,
                        input_label_props: {
                          children: 'Новый пароль',
                        },
                        input_props: {
                          ...adaptedErrors['password1'],
                          onChange: () => {
                            methods.trigger('password2');
                          },
                        },
                      }}
                    ></FormPassword>

                    <FormPassword
                      name="password2"
                      rules={{ required: true }}
                      inner_props={{
                        className: style.field,
                        input_label_props: {
                          children: 'Повторите пароль',
                        },
                        input_props: {
                          ...adaptedErrors['password2'],
                        },
                      }}
                    ></FormPassword>
                  </>
                )}
              </>
            )}
            <SubmitButton
              wrapper={{
                loading: isPending,
                className: cx(style.submit, 'adaptive-password__submit'),
              }}
            >
              {!isPending && buttonText[type][step]}
            </SubmitButton>
          </form>
        </FormProvider>
      </div>
      <Footer></Footer>
    </div>
  );
}
